import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, Grid, CircularProgress, IconButton,
  Tooltip, TextField, Collapse, Paper, MenuItem,
} from '@mui/material';
import { Home, FilterList, Print } from '@mui/icons-material';
import axios from 'axios';
import { fmt as fmtAmt, sym, locale } from '../utils/currency';
import { isRealDate } from '../utils/dateValidation';
import CompanyHeader from './CompanyHeader';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => new Date(d).toLocaleDateString(locale(), { year: 'numeric', month: 'long', day: 'numeric' });

const LearnerLedger = () => {
  const navigate = useNavigate();
  const { admNo } = useParams();

  const [ledger,       setLedger]       = useState(null);
  const [rawInvoices,  setRawInvoices]  = useState([]);
  const [rawReceipts,  setRawReceipts]  = useState([]);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [showFilters,  setShowFilters]  = useState(false);
  const [startDate,    setStartDate]    = useState('');
  const [endDate,      setEndDate]      = useState('');
  const [docTypeFilter,setDocTypeFilter]= useState('all');
  const [stages,       setStages]       = useState([]);
  const [stageFilter,  setStageFilter]  = useState('');

  // print state
  const [printDoc,     setPrintDoc]     = useState(null); // { type: 'Invoice'|'Payment', data: {...} }

  // listen for afterprint to clear printDoc
  useEffect(() => {
    const clear = () => setPrintDoc(null);
    window.addEventListener('afterprint', clear);
    return () => window.removeEventListener('afterprint', clear);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/stages`, { headers: hdrs() })
      .then(r => setStages(r.data || []))
      .catch(() => {});
  }, []);

  const fetchLedger = useCallback(async () => {
    setLoading(true); setError(''); setLedger(null);
    try {
      const res = await axios.get(`${API}/api/finance/statements/${admNo}`, { headers: hdrs() });
      const { learner, invoices, receipts, advanceCredits } = res.data;

      setRawInvoices(invoices || []);
      setRawReceipts(receipts || []);

      const transactions = [
        ...(invoices || []).map(inv => ({
          date:       new Date(inv.date),
          type:       'Invoice',
          documentNo: inv.invoiceNo,
          description: inv.description,
          debit:      inv.amount,
          credit:     0,
          stage:      inv.stage || learner.stage,
        })),
        ...(receipts || []).map(rec => ({
          date:       new Date(rec.paymentDate),
          type:       'Payment',
          documentNo: rec.receiptNo,
          description: rec.transactionDescription,
          debit:      0,
          credit:     rec.amount,
          stage:      learner.stage,
        })),
        // Overpayment held on account (batch-receipt surplus) — a credit that
        // pushes the running balance negative so the overpayment is visible.
        ...(advanceCredits || []).map(c => ({
          date:       new Date(c.date),
          type:       'Overpayment',
          documentNo: c.batchNo || '',
          description: 'Overpayment held on account (advance)',
          debit:      0,
          credit:     c.remainingBalance,
          stage:      learner.stage,
        })),
      ].sort((a, b) => a.date - b.date);

      let balance = 0;
      const entries = transactions.map(t => {
        balance += t.debit - t.credit;
        return { ...t, balance };
      });

      setLedger({ learner, entries });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch ledger');
    } finally {
      setLoading(false);
    }
  }, [admNo]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const handlePrint = (entry) => {
    if (entry.type === 'Invoice') {
      const inv = rawInvoices.find(i => i.invoiceNo === entry.documentNo);
      if (inv) { setPrintDoc({ type: 'Invoice', data: inv }); setTimeout(() => window.print(), 60); }
    } else {
      const rec = rawReceipts.find(r => r.receiptNo === entry.documentNo);
      if (rec) { setPrintDoc({ type: 'Payment', data: rec }); setTimeout(() => window.print(), 60); }
    }
  };

  const filtered = () => {
    if (!ledger) return [];
    let list = [...ledger.entries];
    if (startDate) list = list.filter(e => e.date >= new Date(startDate));
    if (endDate)   list = list.filter(e => e.date <= new Date(endDate));
    if (stageFilter) list = list.filter(e => e.stage === stageFilter);
    if (docTypeFilter !== 'all') list = list.filter(e => e.type.toLowerCase() === docTypeFilter);
    return list;
  };

  const rows         = filtered();
  const totalDebit   = rows.reduce((s, e) => s + e.debit,  0);
  const totalCredit  = rows.reduce((s, e) => s + e.credit, 0);
  const finalBalance = rows.length > 0 ? rows[rows.length - 1].balance : 0;

  const pd = printDoc?.data;

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #ledger-print-area, #ledger-print-area * { visibility: visible; }
          #ledger-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 40px; box-sizing: border-box; font-family: serif; }
        }
        @media screen { #ledger-print-area { display: none; } }
      `}</style>

      {/* Hidden print area — renders whichever doc was selected */}
      {printDoc && pd && (
        <Box id="ledger-print-area">
          {printDoc.type === 'Invoice' ? (
            <>
              <CompanyHeader title="Invoice" subtitle={pd.invoiceNo} />
              <Box textAlign="center" mb={2}>
                <Typography variant="body2">Date: {fmtDate(pd.date)}</Typography>
              </Box>
              <Box sx={{ border: '1px solid #ccc', p: 2, mb: 2 }}>
                <Typography variant="body2"><strong>Billed To:</strong> {pd.learnerAdmNo}</Typography>
                <Typography variant="body2"><strong>Stage:</strong> {pd.stage || '—'}</Typography>
              </Box>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Description</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Reason / Ref</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>Amount ({sym()})</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{pd.description}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{pd.reason || '—'}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{fmtAmt(pd.amount)}</td>
                  </tr>
                  <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>TOTAL</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{sym()} {fmtAmt(pd.amount)}</td>
                  </tr>
                </tbody>
              </table>
              <Typography variant="caption">GL Account: {pd.glAccount || '—'}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>Status: <strong>{pd.status}</strong></Typography>
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">Prepared by: _______________________</Typography>
                <Typography variant="caption">Authorized by: _______________________</Typography>
              </Box>
            </>
          ) : (
            <>
              <CompanyHeader title="Payment Receipt" subtitle={pd.receiptNo} />
              <Box textAlign="center" mb={2}>
                <Typography variant="body2">Date: {fmtDate(pd.paymentDate)}</Typography>
              </Box>
              <Box sx={{ border: '1px solid #ccc', p: 2, mb: 2 }}>
                <Typography variant="body2"><strong>Received From:</strong> {pd.learnerAdmNo}</Typography>
                <Typography variant="body2"><strong>For Invoice:</strong> {pd.invoiceNo}</Typography>
              </Box>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Description</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Reference</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>Amount ({sym()})</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{pd.transactionDescription}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{pd.reference}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{fmtAmt(pd.amount)}</td>
                  </tr>
                  <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                    <td colSpan={2} style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>TOTAL RECEIVED</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{sym()} {fmtAmt(pd.amount)}</td>
                  </tr>
                </tbody>
              </table>
              <Typography variant="caption">Bank: {pd.bankAccountNo}</Typography><br />
              <Typography variant="caption">GL Account: {pd.glAccount || '—'}</Typography>
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">Received by: _______________________</Typography>
                <Typography variant="caption">Authorized by: _______________________</Typography>
              </Box>
            </>
          )}
        </Box>
      )}

      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6 }}>
        <Card elevation={6}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={10}>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  Learner Ledger — {ledger?.learner.name} ({ledger?.learner.admNo})
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ textAlign: 'right' }}>
                <Tooltip title="Toggle Filters">
                  <IconButton onClick={() => setShowFilters(f => !f)}>
                    <FilterList />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Back to Dashboard">
                  <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                    <Home />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}><CircularProgress /></Box>}
            {error   && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {ledger && (
              <>
                <Collapse in={showFilters}>
                  <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <TextField label="Start Date" type="date" value={startDate}
                          onChange={e => setStartDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }}
                          error={!!(startDate && !isRealDate(startDate))}
                          helperText={startDate && !isRealDate(startDate) ? 'Not a valid date' : ''} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField label="End Date" type="date" value={endDate}
                          onChange={e => setEndDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }}
                          inputProps={{ min: startDate || undefined }}
                          error={!!((endDate && !isRealDate(endDate)) || (startDate && endDate && endDate < startDate))}
                          helperText={(endDate && !isRealDate(endDate)) ? 'Not a valid date' : (startDate && endDate && endDate < startDate) ? 'End before start' : ''} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField select label="Stage" value={stageFilter}
                          onChange={e => setStageFilter(e.target.value)} fullWidth>
                          <MenuItem value="">All Stages</MenuItem>
                          {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField select label="Document Type" value={docTypeFilter}
                          onChange={e => setDocTypeFilter(e.target.value)} fullWidth>
                          <MenuItem value="all">All Transactions</MenuItem>
                          <MenuItem value="invoice">Invoices Only</MenuItem>
                          <MenuItem value="payment">Payments Only</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </Paper>
                </Collapse>

                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Document No</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Debit</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Credit</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Balance</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Print</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">No ledger entries for selected filters</TableCell>
                        </TableRow>
                      ) : rows.map((entry, idx) => (
                        <TableRow key={`${entry.documentNo}-${idx}`} hover
                          sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                          <TableCell>{entry.date.toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}
                              sx={{ color: entry.type === 'Invoice' ? 'error.main' : 'success.main' }}>
                              {entry.type}
                            </Typography>
                          </TableCell>
                          <TableCell>{entry.documentNo}</TableCell>
                          <TableCell>{entry.description}</TableCell>
                          <TableCell align="right" sx={{ color: entry.debit > 0 ? 'error.main' : 'text.secondary' }}>
                            {entry.debit > 0 ? fmtAmt(entry.debit) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: entry.credit > 0 ? 'success.main' : 'text.secondary' }}>
                            {entry.credit > 0 ? fmtAmt(entry.credit) : '—'}
                          </TableCell>
                          <TableCell align="right" fontWeight="bold"
                            sx={{ color: entry.balance >= 0 ? 'error.main' : 'success.main' }}>
                            {fmtAmt(entry.balance)}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`Print ${entry.type}`}>
                              <IconButton size="small" onClick={() => handlePrint(entry)}>
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.200' }}>
                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>TOTALS:</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{fmtAmt(totalDebit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>{fmtAmt(totalCredit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: finalBalance >= 0 ? 'error.main' : 'success.main' }}>
                          {fmtAmt(finalBalance)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default LearnerLedger;
